/**
 * UI-side mapping of teleop-session status codes → localized text.
 *
 * The transport module only emits stable string codes. All human-readable labels live
 * HERE, in the UI layer, so a different host app can map the same codes to its own strings.
 */

import { SessionPhase, STATUS_CODES } from "../modules/teleop-session";
import type { SessionPhaseValue, StatusCodeValue } from "../modules/teleop-session";

export const STATUS_LABELS: Record<StatusCodeValue, string> = {
  "missing-config": "Некорректный конфиг сессии (нет sessionId / signalingWsBase)",
  "signaling-connecting": "Подключение к signaling (WebSocket)…",
  "mediasoup-loading": "Signaling OK, загрузка mediasoup-client…",
  "in-room-waiting-video": "В комнате. Ожидание видео с робота…",
  "no-video-producer": "В комнате нет источника видео (стример не подключён)",
  "session-active": "Сессия активна",
  "reconnecting-media": "Переподключение медиа-сессии…",
  rejoined: "Сессия переподключена",
  "rejoin-failed": "Переподключение не удалось",
  "ice-restored": "ICE восстановлен",
  "ws-reconnecting": "Signaling: переподключение…",
  "consumer-setup-failed": "Ошибка настройки consumer'а",
  "consumer-notif-failed": "Ошибка обработки consumer-уведомления",
  "consumer-attaching": "Подключение видео-слота…",
  "video-slot-active": "Видео-слот активен",
  "command-channel-unavailable": "Канал команд недоступен",
};

export function statusCodeToLabel(code: StatusCodeValue | string): string {
  return STATUS_LABELS[code as StatusCodeValue] ?? code;
}

/**
 * The persistent status line, once a session exists. Raw `status` is fine-grained by
 * design (rejoined/ice-restored/video-slot-active/consumer-attaching/…) and nothing
 * ever re-emits "session-active" after a recovery — so read literally, it gets stuck
 * on whatever recovery step happened to fire last instead of settling back to "Сессия
 * активна" the way a fresh connect does. Once the session has actually settled (phase
 * ACTIVE, video not stalled), collapse any of those codes to that same label; everywhere
 * else (still connecting, mid-recovery, video stalled) keep showing the raw code's own
 * text so real progress/errors stay visible.
 */
export function deriveStatusText(
  status: StatusCodeValue | null,
  phase: SessionPhaseValue,
  videoStalled: boolean
): string {
  if (status == null) return "Загрузка модулей WebRTC…";
  if (phase === SessionPhase.ACTIVE && !videoStalled) {
    return STATUS_LABELS[STATUS_CODES.SESSION_ACTIVE];
  }
  return statusCodeToLabel(status);
}
